import React from 'react';

interface TableSkeletonProps {
  /** Number of skeleton rows to render. */
  rows?: number;
  /** Column widths, in percent of row width. Length determines column count. */
  columns?: number[];
  /** When provided, renders cells right-aligned with a "num" class. */
  numericColumns?: number[];
}

/**
 * Drop-in <tbody> placeholder for hf-table rows.
 * Mirrors the parent table layout — render inside a real <table className="hf-table">.
 */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 6,
  columns = [40, 24, 16, 12, 8],
  numericColumns = []
}) => {
  const numSet = new Set(numericColumns);
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={`skel-${r}`} className="hf-skel-row" aria-hidden="true">
          {columns.map((w, c) => (
            <td key={c} className={numSet.has(c) ? 'num' : undefined}>
              <span className="hf-skel line" style={{ width: `${w}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

/** Plain block skeleton for grid/card lists (not inside a table). */
export const CardSkeleton: React.FC<{ count?: number; height?: number }> = ({
  count = 8,
  height = 200
}) => (
  <div className="hf-model-grid" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="hf-skel"
        style={{ height, borderRadius: 'var(--r-2)' }}
      />
    ))}
  </div>
);

/** KPI strip skeleton — N cards in a flex row. */
export const KpiSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="hf-kpi-strip" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="hf-kpi">
        <span className="hf-skel line sm" style={{ width: '60%' }} />
        <span className="hf-skel kpi-value" />
        <span className="hf-skel kpi-sub" />
      </div>
    ))}
  </div>
);
